import { Box, Stack, Typography, useTheme } from '@mui/material';
import { useFormik } from 'formik';
import { entries, isArray } from 'lodash';
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';

// Components
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import FormikForm from 'src/components/common/FormikForm';
import ModalComponent from 'src/components/Modal/ModalComponent';
import FormItemChooseCompany from 'src/features/CompareCompany/Common/FormItemChooseCompany';

import CreditCostIndicator from 'src/components/common/CreditCostIndicator';
import FormItemJurisdictionSingle from 'src/components/Form/FormItemJurisdictionSingle';
import { TypeCompareCompany } from 'src/features/CompareCompany/Common/config';
import { useFeatureAccess } from 'src/hooks/useFeatureAccess';
import { AuthRouteKeys } from 'src/utils/constants/routes';
import { URL_ENDPOINT } from 'src/utils/constants/service';
import { getStringOrDefault } from 'src/utils/helpers/stringHelpers';
import { useFormSearch } from 'src/zustand/useFormSearch';
import { FeatureAction } from 'src/utils/constants/featuresPoint';

interface IProps {
  open: boolean;
  selectedCompanyCompare: ICompareCompany | null;
  handleClose: () => void;
}

const FormCompareCompany: React.FC<IProps> = ({ open, selectedCompanyCompare, handleClose }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { resetFormCompare } = useFormSearch((state) => state);
  const { getFeaturePoints } = useFeatureAccess();

  const pointsViewCompareCompanies = getFeaturePoints(URL_ENDPOINT.company.companies + 'compare-companies', FeatureAction.VIEW);

  const formik = useFormik<TypeCompareCompany>({
    enableReinitialize: true,
    initialValues: {
      company_1: '',
      company_2: '',
      company_3: '',
      jurisdiction_1: '',
      jurisdiction_2: '',
      jurisdiction_3: '',
    },
    validationSchema: Yup.object().shape({
      company_1: Yup.string().max(255).required('This field is required'),
      company_2: Yup.string().max(255).required('This field is required'),
    }),
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      handleClose();
      setSubmitting(false);
      const params = entries({
        company_1: values.company_1,
        company_2: values.company_2,
        company_3: values.company_3,
      })
        .map(([key, value]) => {
          if (isArray(value)) {
            const q = value.map((val: string) => {
              return `companyIds=${val}`;
            });
            return q.join('&');
          } else {
            return `companyIds=${value}`;
          }
        })
        .join('&');

      navigate({ pathname: AuthRouteKeys.CompareCompany, search: encodeURI(params) });
      resetForm();
      resetFormCompare();
    },
  });

  const selectedOptions = [formik.values.company_1, formik.values.company_2, formik.values.company_3].filter(
    Boolean,
  ) as string[];

  useEffect(() => {
    if (selectedCompanyCompare) {
      formik.setFieldValue('company_1', selectedCompanyCompare?.id || '');
    }
  }, [selectedCompanyCompare]);

  const { values } = formik;

  return (
    <ModalComponent padding={4} handleClose={handleClose} open={open}>
      <FormikForm formik={formik}>
        <Box width={600}>
          <Typography textAlign={'center'} variant="h5">
            Compare Companies
          </Typography>
          <Box mt={1} mb={4}>
            <Typography textAlign={'center'} color={theme.palette.text.secondary} variant="body1">
              Please select the company profiles you want to compare.
            </Typography>
          </Box>
          <Stack direction={'column'} spacing={2}>
            <Stack direction={'row'} spacing={1}>
              <FormItemJurisdictionSingle
                keySelected={'jurisdiction'}
                selected={values.jurisdiction_1}
                onChange={(_, value: IJurisdictions | null) => {
                  formik.setFieldValue('jurisdiction_1', value?.jurisdiction);
                }}
                inputProps={{
                  label: 'Jurisdiction',
                  required: true,
                }}
                sx={{ width: 400 }}
              />

              <FormItemChooseCompany
                defaultParams={{
                  is_registered_number: true,
                }}
                jurisdictions={[getStringOrDefault(values.jurisdiction_1)]}
                id="company_1"
                required
                defaultValue={selectedCompanyCompare}
                label="Company Name 1"
                disabledList={selectedOptions}
                onChange={(value) => formik.setFieldValue('company_1', value)}
              />
            </Stack>

            <Stack direction={'row'} spacing={1}>
              <FormItemJurisdictionSingle
                keySelected={'jurisdiction'}
                selected={values.jurisdiction_2}
                onChange={(_, value: IJurisdictions | null) => {
                  formik.setFieldValue('jurisdiction_2', value?.jurisdiction);
                }}
                inputProps={{
                  label: 'Jurisdiction',
                  required: true,
                }}
                sx={{ width: 400 }}
              />
              <FormItemChooseCompany
                defaultParams={{
                  is_registered_number: true,
                }}
                jurisdictions={[getStringOrDefault(values.jurisdiction_2)]}
                id="company_2"
                required
                defaultValue={null}
                label="Company Name 2"
                disabledList={selectedOptions}
                onChange={(value) => formik.setFieldValue('company_2', value)}
              />
            </Stack>

            <Stack direction={'row'} spacing={1}>
              <FormItemJurisdictionSingle
                keySelected={'jurisdiction'}
                selected={values.jurisdiction_3}
                onChange={(_, value: IJurisdictions | null) => {
                  formik.setFieldValue('jurisdiction_3', value?.jurisdiction);
                }}
                inputProps={{
                  label: 'Jurisdiction',
                }}
                sx={{ width: 400 }}
              />
              <FormItemChooseCompany
                defaultParams={{
                  is_registered_number: true,
                }}
                jurisdictions={[getStringOrDefault(values.jurisdiction_3)]}
                id="company_3"
                defaultValue={null}
                label="Company Name 3"
                disabledList={selectedOptions}
                onChange={(value) => formik.setFieldValue('company_3', value)}
              />
            </Stack>

            <ButtonWithLoading
              startIcon={<CreditCostIndicator pointData={pointsViewCompareCompanies} />}
              sx={{ height: 53 }}
              size="large"
              variant="contained"
              isRounded
              type="submit"
            >
              Search Now
            </ButtonWithLoading>
          </Stack>
        </Box>
      </FormikForm>
    </ModalComponent>
  );
};

export default FormCompareCompany;
